import {
  hashKey,
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type Query,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import type { ReportService } from "@/services/report.service";
import type {
  FieldListResponse,
  Report,
  ReportObjectType,
  ReportRun,
  ReportSnapshotResponse,
  ReportsPage,
} from "@/types/report";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopeIfReady, scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The caller's reports (Insights → Reports, NG-2811), every read under
 * `[slug, uid, 'reports', …]`:
 *
 * - **list**, `GET {ws}/reports/`: the Reports page. A Guest holds no
 *   `report.list` and gets a 403, which the page shows as no access.
 * - **detail**, `GET {ws}/reports/{id}/`: the Report View header, the
 *   breadcrumb and the builder's edit draft. A 404 (someone else's report, or
 *   a deleted one) and a 403 (no `report.read`) are answers the view draws.
 * - **snapshot**, `GET {ws}/reports/{id}/snapshot/`, under the detail: the
 *   latest run's rows, one cursor page at a time, keyed by the sort, so a
 *   re-sort reads its own pages and a late answer for the old sort lands on
 *   its own entry. A 404 means the report was never run, and is a page of its
 *   own (`null`), not an error.
 * - **fields**, `GET {ws}/reports/fields/?object_types=`: the builder's field
 *   catalog for one set of object types.
 *
 * Reports are private to their owner, so another person never changes them and
 * no hint re-reads another tab. The list, detail and fields are **reference**
 * data: they change only through a report write, and every report writer puts
 * its answer into, or drops, the entries it changes. The snapshot changes only
 * when a run lands: it is read again on every visit, as the store did, and
 * polled every 2 s only while a run is still generating, never in a hidden tab.
 * Each read is a plain idempotent GET, so it declares `"safe-read"`.
 *
 * @writers `useReportWriters` (`hooks/use-report-writers.ts`): report create,
 * update, duplicate, delete and run.
 */

type Reads = Pick<ReportService, "list" | "retrieve" | "fetchFields" | "fetchSnapshot">;

/** Rows per snapshot page. The table pages through the run server-side. */
const SNAPSHOT_PER_PAGE = 100;

/** Poll interval (ms) while a run is `pending`/`running`. */
const RUN_POLL_INTERVAL_MS = 2000;

/** One page of a snapshot, or `null` for a report that has never been run. */
export type SnapshotPage = ReportSnapshotResponse | null;

/** A snapshot sort: a resolved column key and its direction. */
export interface SnapshotSort {
  sort: string;
  order: "asc" | "desc";
}

/** The HTTP status a rejected API call carries, if any. */
export function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/** A run whose status is still settling (the table shows a generating state). */
export function runInProgress(run: ReportRun | undefined): boolean {
  return run?.status === "pending" || run?.status === "running";
}

/** `[slug, uid, 'reports', 'list']`. */
export function reportListKey(scope: QueryScope) {
  return scopedKey(scope, "reports", "list");
}

/** The caller's own reports: one page, which the private list never outgrows. */
export function reportListQuery(service: Pick<Reads, "list">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    // A Guest holds no `report.list`: the page draws its no-access state.
    meta: { ...FRESHNESS.reference.meta, expectedStatuses: [403] },
    queryKey: reportListKey(scope),
    queryFn: scopedRead((request) => service.list(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * Start the list read for the route loader, so the request leaves with the
 * navigation. Never rejects: a refusal is the page's to draw.
 */
export async function prefetchReportList(
  service: Pick<Reads, "list">,
  slug: string,
): Promise<void> {
  const scope = scopeIfReady(slug, "report list");
  if (scope) await getQueryClient().prefetchQuery(reportListQuery(service, scope));
}

/** `[slug, uid, 'reports', 'detail', id]`: the report's snapshots sit under it. */
export function reportDetailKey(scope: QueryScope, reportId: string) {
  return scopedKey(scope, "reports", "detail", reportId);
}

/** One report: its name, object types, columns and filters. */
export function reportDetailQuery(
  service: Pick<Reads, "retrieve">,
  scope: QueryScope,
  reportId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    // Someone else's report, a deleted one (404) or no `report.read` (403):
    // the view draws its not-found state.
    meta: { ...FRESHNESS.reference.meta, expectedStatuses: [403, 404] },
    queryKey: reportDetailKey(scope, reportId),
    queryFn: scopedRead((request) => service.retrieve(scope.tenant, reportId, request), {
      retry: "safe-read",
    }),
  });
}

/** `[slug, uid, 'reports', 'fields', types]`: `types` sorted, so order never splits an entry. */
export function reportFieldsKey(scope: QueryScope, objectTypes: readonly ReportObjectType[]) {
  return scopedKey(scope, "reports", "fields", [...objectTypes].sort().join(","));
}

/** The field catalog for one set of object types. */
export function reportFieldsQuery(
  service: Pick<Reads, "fetchFields">,
  scope: QueryScope,
  objectTypes: readonly ReportObjectType[],
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: reportFieldsKey(scope, objectTypes),
    queryFn: scopedRead(
      (request): Promise<FieldListResponse> =>
        service.fetchFields(scope.tenant, [...objectTypes], request),
      { retry: "safe-read" },
    ),
  });
}

/** Every snapshot cached for one report, whatever its sort. */
export function reportSnapshotsKey(scope: QueryScope, reportId: string) {
  return scopedKey(scope, "reports", "detail", reportId, "snapshot");
}

/** `[…, 'snapshot', sort]`: `""` is the run's default order. */
export function reportSnapshotKey(
  scope: QueryScope,
  reportId: string,
  sort: SnapshotSort | undefined,
) {
  return scopedKey(
    scope,
    "reports",
    "detail",
    reportId,
    "snapshot",
    sort ? `${sort.sort}:${sort.order}` : "",
  );
}

type SnapshotData = InfiniteData<SnapshotPage, string | null>;

/** The run the first page carries: the latest run, as the snapshot last answered it. */
export function snapshotRun(
  data: Pick<InfiniteData<SnapshotPage, unknown>, "pages"> | undefined,
): ReportRun | undefined {
  return data?.pages[0]?.run;
}

function runStillGenerating(query: Query<SnapshotPage, Error, SnapshotData, QueryKey>): boolean {
  return runInProgress(snapshotRun(query.state.data));
}

/**
 * One report's snapshot under one sort, a page at a time.
 *
 * `onSortRejected` runs when the server refuses the sort with a 400, which it
 * does when the sort names a column the latest run does not have. The stored
 * sort is sticky (it is restored on every visit), so the caller drops it: the
 * key moves to the unsorted entry and the table loads unsorted, one extra
 * request, as before. Only a 400: a 5xx or a dropped connection says nothing
 * about the sort, and must not cost the user a stored preference. The server
 * validates a sort only against a complete run (NG-632), so a run still
 * generating never rejects one.
 */
export function reportSnapshotQuery(
  service: Pick<Reads, "fetchSnapshot">,
  scope: QueryScope,
  reportId: string,
  sort: SnapshotSort | undefined,
  onSortRejected: () => void,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    // Read again on every visit, as the store did: the rows change only when a
    // run lands, and a run is the one thing a visit checks for.
    staleTime: 0,
    // Polled only while the run is still generating; Query skips a hidden tab
    // and reads at once when it is shown again (NG-2816).
    refetchInterval: (query) => (runStillGenerating(query) ? RUN_POLL_INTERVAL_MS : false),
    refetchOnWindowFocus: runStillGenerating,
    // 400: the sort was refused and is dropped (above). 403: no `report.read`,
    // which the detail read draws.
    meta: { ...FRESHNESS.mutable.meta, expectedStatuses: [400, 403] },
    queryKey: reportSnapshotKey(scope, reportId, sort),
    initialPageParam: null as string | null,
    queryFn: scopedRead<SnapshotPage, string | null>(
      async (request, { pageParam }) => {
        try {
          return await service.fetchSnapshot(
            scope.tenant,
            reportId,
            {
              cursor: pageParam,
              perPage: SNAPSHOT_PER_PAGE,
              sort: sort?.sort ?? null,
              order: sort?.order ?? null,
            },
            request,
          );
        } catch (error) {
          const status = httpStatus(error);
          // Never run yet: the view offers to run it.
          if (status === 404 && pageParam === null) return null;
          if (status === 400 && sort && pageParam === null) {
            console.warn("[reports] the snapshot refused the stored sort; reading it unsorted", {
              reportId,
              sort,
              error,
            });
            onSortRejected();
          }
          throw error;
        }
      },
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: SnapshotPage, _all, cursor) =>
      last?.rows.next_page_results && last.rows.next_cursor && last.rows.next_cursor !== cursor
        ? last.rows.next_cursor
        : undefined,
    // A re-sort keeps the rows on screen until the sorted page lands, but never
    // carries one report's rows into another's view.
    placeholderData: (previous, previousQuery) =>
      previousQuery &&
      hashKey(previousQuery.queryKey.slice(0, -1)) === hashKey(reportSnapshotsKey(scope, reportId))
        ? previous
        : undefined,
  });
}

/**
 * Start the snapshot read for the route loader, without waiting on it. The
 * sort is the one the loader has just restored, so the first request is
 * already sorted.
 */
export function prefetchReportSnapshot(
  service: Reads,
  slug: string,
  reportId: string,
  sort: SnapshotSort | undefined,
  onSortRejected: () => void,
): void {
  const scope = scopeIfReady(slug, "report snapshot");
  if (!scope) return;
  const client = getQueryClient();
  void client.prefetchQuery(reportDetailQuery(service, scope, reportId));
  void client.prefetchInfiniteQuery(
    reportSnapshotQuery(service, scope, reportId, sort, onSortRejected),
  );
}

/** The mutation key every run of one report goes under: what `running` watches. */
export function reportRunKey(scope: QueryScope, reportId: string) {
  return scopedKey(scope, "reports", "run", reportId);
}

// ---- Cache edits the writers make ----------------------------------------

/** After a write that answered with the report: show it on its detail and its list row. */
export function primeReport(client: QueryClient, scope: QueryScope, report: Report): void {
  client.setQueryData(reportDetailKey(scope, report.id), report);
  client.setQueryData<ReportsPage>(reportListKey(scope), (page) =>
    page ? { ...page, results: page.results.map((r) => (r.id === report.id ? report : r)) } : page,
  );
}

/** After a create or duplicate: list the new report at once, and seed its detail. */
export function appendReport(client: QueryClient, scope: QueryScope, report: Report): void {
  client.setQueryData(reportDetailKey(scope, report.id), report);
  client.setQueryData<ReportsPage>(reportListKey(scope), (page) =>
    page && !page.results.some((r) => r.id === report.id)
      ? { ...page, results: [...page.results, report], total_count: page.total_count + 1 }
      : page,
  );
}

/** After a delete: drop the report's detail, its snapshots and its list row. */
export function forgetReport(client: QueryClient, scope: QueryScope, reportId: string): void {
  client.removeQueries({ queryKey: reportDetailKey(scope, reportId) });
  client.setQueryData<ReportsPage>(reportListKey(scope), (page) =>
    page?.results.some((r) => r.id === reportId)
      ? {
          ...page,
          results: page.results.filter((r) => r.id !== reportId),
          total_count: Math.max(0, page.total_count - 1),
        }
      : page,
  );
}

/**
 * After a run was accepted: throw away every page of every sort, which belong
 * to the old run, and read the first page of the one on screen again. Settles
 * once that read has.
 */
export function restartReportSnapshots(
  client: QueryClient,
  scope: QueryScope,
  reportId: string,
): Promise<void> {
  return client.resetQueries({ queryKey: reportSnapshotsKey(scope, reportId) });
}
